/**
 * TIPOS DE CONTENEDOR PAGE — Catálogo de tubos y recipientes para muestras
 *
 * Ubicación en sidebar: Administración → Catálogo → Tipos de Contenedor
 * (junto a Exámenes y Perfiles)
 */

import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  TestTubes,
  RefreshCw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import {
  useTiposContenedor,
  useDeactivateTipoContenedor,
  useDeleteTipoContenedor,
  type TipoContenedor,
} from "@/hooks/use-muestras";

import { TipoContenedorFormDialog } from "./components/tipo-contenedor-form-dialog";

export default function TiposContenedorPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TipoContenedor | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: "deactivate" | "activate" | "delete";
    item: TipoContenedor;
  } | null>(null);

  const { data: tiposData, isLoading, error, refetch } = useTiposContenedor();
  const items = tiposData?.results ?? [];

  const deactivateMutation = useDeactivateTipoContenedor({
    onSuccess: () => {
      toast.success(t("configuracion.tiposContenedor.desactivado"));
      setConfirmAction(null);
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteTipoContenedor({
    onSuccess: () => {
      toast.success(t("configuracion.tiposContenedor.eliminado"));
      setConfirmAction(null);
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleEdit = useCallback((item: TipoContenedor) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setSelectedItem(null);
    setIsFormOpen(true);
  }, []);

  const handleFormSuccess = useCallback(() => {
    setIsFormOpen(false);
    setSelectedItem(null);
    refetch();
  }, [refetch]);

  return (
    <div className="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <TestTubes className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">
            {t("configuracion.tiposContenedor.titulo")}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t("configuracion.tiposContenedor.descripcion")}
          </p>
        </div>
      </div>

      {/* Contenido */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{items.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-1">{t("common.refresh")}</span>
            </Button>
            <Button size="sm" onClick={handleNew}>
              <Plus className="h-4 w-4 mr-1" />
              {t("configuracion.tiposContenedor.nuevo")}
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {getErrorMessage(error as Error)}
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">{t("common.loading")}</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {t("configuracion.tiposContenedor.sin_datos")}
              </div>
            ) : (
              <div className="divide-y">
                {/* Encabezados — solo desktop */}
                <div className="hidden md:grid md:grid-cols-[1fr_120px_130px_80px_80px_48px] gap-3 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide bg-muted/30">
                  <span>{t("configuracion.tiposContenedor.fields.nombre")}</span>
                  <span>{t("configuracion.tiposContenedor.fields.tipo_muestra")}</span>
                  <span>{t("configuracion.tiposContenedor.fields.aditivo")}</span>
                  <span>{t("configuracion.tiposContenedor.fields.volumen")}</span>
                  <span>{t("common.status")}</span>
                  <span />
                </div>
                {items.map((item) => (
                  <div key={item.id} className="hover:bg-muted/30 transition-colors">
                    {/* Desktop: grid */}
                    <div className="hidden md:grid md:grid-cols-[1fr_120px_130px_80px_80px_48px] gap-3 px-4 py-3 items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-gray-300 shrink-0"
                          style={{ backgroundColor: item.color }}
                          title={item.color}
                        />
                        <span className="font-medium truncate">{item.nombre}</span>
                      </div>
                      <span className="text-sm text-muted-foreground truncate">
                        {item.tipo_muestra_display}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {item.aditivo || "—"}
                      </span>
                      <span className="text-sm">{item.volumen_ml} mL</span>
                      <Badge variant={item.is_active ? "default" : "secondary"} className="w-fit">
                        {item.is_active ? t("common.active") : t("common.inactive")}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t("common.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {item.is_active ? (
                            <DropdownMenuItem
                              onClick={() => setConfirmAction({ type: "deactivate", item })}
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              {t("common.deactivate")}
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem
                                onClick={() => setConfirmAction({ type: "activate", item })}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t("common.activate")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setConfirmAction({ type: "delete", item })}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t("common.delete")}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Mobile: tarjeta */}
                    <div className="md:hidden flex items-start justify-between p-4 gap-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-gray-300 shrink-0 mt-1"
                          style={{ backgroundColor: item.color }}
                        />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{item.nombre}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.tipo_muestra_display}
                            {item.aditivo ? ` · ${item.aditivo}` : ""}
                            {` · ${item.volumen_ml} mL`}
                          </p>
                          <Badge
                            variant={item.is_active ? "default" : "secondary"}
                            className="mt-1 text-xs"
                          >
                            {item.is_active ? t("common.active") : t("common.inactive")}
                          </Badge>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t("common.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {item.is_active ? (
                            <DropdownMenuItem
                              onClick={() => setConfirmAction({ type: "deactivate", item })}
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              {t("common.deactivate")}
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem
                                onClick={() => setConfirmAction({ type: "activate", item })}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t("common.activate")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setConfirmAction({ type: "delete", item })}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t("common.delete")}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Form Dialog */}
      <TipoContenedorFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        item={selectedItem}
        onSuccess={handleFormSuccess}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={
          confirmAction?.type === "delete"
            ? t("configuracion.tiposContenedor.confirmar_eliminar")
            : confirmAction?.type === "activate"
              ? t("configuracion.tiposContenedor.confirmar_activar")
              : t("configuracion.tiposContenedor.confirmar_desactivar")
        }
        description={confirmAction?.item?.nombre ?? ""}
        variant={confirmAction?.type === "delete" ? "destructive" : "default"}
        isLoading={deactivateMutation.isPending || deleteMutation.isPending}
        onConfirm={() => {
          if (!confirmAction) return;
          if (confirmAction.type === "delete") {
            deleteMutation.mutate(confirmAction.item.id);
          } else {
            deactivateMutation.mutate(confirmAction.item.id);
          }
        }}
      />
    </div>
  );
}
